import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const peel: Competitor = {
  "id": "peel",
  "name": "Peel Insights",
  "group": "ecomm",
  "website": "https://www.peelinsights.com/",
  "owner": "Unclear. Shopify acqui-hired Peel's team in May 2024 (CEO Nicolas Grasset joined Shopify); the Peel product still operates and sells plans in 2026, but who owns and runs it now is not publicly confirmed.",
  "summary": "Peel is a retention and cohort analytics tool for Shopify brands, with heavy focus on subscription businesses. It turns Shopify, Amazon, subscription and marketing data into prebuilt cohort, LTV, segment and attribution reports, plus an AI dashboard generator called Magic Dash.",
  "bestFor": [
    "Shopify DTC brands, especially subscription brands on Recharge, Skio, Smartrr, Stay.ai or similar, that want deep cohort and retention metrics without building them",
    "Retention and CRM teams that want RFM and custom segments they can push to email and ad tools",
    "Brands that want a Snowflake copy of their Peel data for SQL without running their own pipelines"
  ],
  "notFor": [
    "Brands with large wholesale, retail or ERP channels (no NetSuite, Faire or POS connectors found)",
    "TikTok Shop sellers who need TikTok Shop order data (no TikTok Shop connector found; TikTok ads only)",
    "Buyers who need certainty about vendor roadmap and ownership, given the 2024 team acqui-hire by Shopify"
  ],
  "strengths": [
    "30+ prebuilt cohort KPIs, product metrics and customer-journey views on day one, which a custom dbt + Sigma build would need to model and test",
    "Native connectors for six subscription platforms (Recharge, Skio, Smartrr, Awtomic, Bold, Stay.ai), a wider subscription set than most custom builds start with",
    "Peel states store setup takes under 5 minutes and initial data processing about 1-3 days, versus weeks for a custom warehouse",
    "Snowflake data access for SQL queries is included in every plan, so analysts can still go beyond the dashboards",
    "Entry price from $179/month (billed annually), far below consultant plus warehouse plus BI license costs",
    "Daily Slack/email digests, scheduled CSV reports and exportable RFM/custom segments are built in"
  ],
  "limitations": [
    "No TikTok Shop, NetSuite, Faire or retail POS connectors appear in Peel's pricing page or integration docs",
    "Peel does not run its own tracking pixel; attribution is built from Shopify's customer-journey referral and UTM data",
    "Data refreshes daily, not in real time, per the pricing page",
    "Custom reports beyond the prebuilt set are rationed by plan (0 credits on Core, 1/month on Essentials, 2 on Accelerate, 5 on Tailored)",
    "Store connections are capped by plan (3 on Essentials, 7 on Accelerate, unlimited only on Tailored)",
    "Magic Dash docs were last updated in mid-2025 and state that your data is shared with Peel's AI"
  ],
  "pricing": {
    "model": "Tiered by monthly store orders",
    "publicStartingPrice": "Core from $179/month billed annually ($199 month-to-month)",
    "freeTier": "No free tier; 7-day free trial (no credit card) that starts once data is downloaded, computed on the last year of data",
    "notes": "Live pricing page lists Core ($179/mo annual, $199 monthly), Essentials ($449 / $499), Accelerate ($809 / $899) and Tailored (custom, 62,000+ monthly orders). Every plan includes Shopify and Amazon, ads, Klaviyo, subscription connectors, segments, cohorts, digests and Snowflake data access; plans differ by order volume, store connections, custom report credits and support level. The Shopify App Store listing shows the same three self-serve plans. PricingSaaS reported Core removed in May 2026, but Core was still on Peel's live pricing page and App Store listing when checked on 2026-11-03. Setup: Peel says connecting sources takes under 5 minutes, dashboards are built within 24 hours, and its getting-started FAQ says initial processing takes about 1-3 days depending on order volume.",
    "sourceUrl": "https://www.peelinsights.com/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Core data source; built as a Shopify app",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon store data and Amazon Ads included in every plan",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "tiktok_shop": {
      "value": "no",
      "note": "No TikTok Shop connector in pricing page or integration docs; TikTok ads only",
      "sourceUrl": "https://help.peelinsights.com"
    },
    "wholesale_retail_or_erp": {
      "value": "no",
      "note": "No ERP, wholesale, 3PL or POS connectors listed",
      "sourceUrl": "https://help.peelinsights.com"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Google, Meta, TikTok, Pinterest and Amazon Ads",
      "sourceUrl": "https://help.peelinsights.com"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Multi-touch attribution from Shopify customer-journey/UTM data; Peel says it does not run its own pixel",
      "sourceUrl": "https://help.peelinsights.com/docs/attribution"
    },
    "mmm_incrementality": {
      "value": "no",
      "note": "No MMM or incrementality testing in docs or pricing page",
      "sourceUrl": "https://help.peelinsights.com/docs/attribution"
    },
    "profit_cogs": {
      "value": "partial",
      "note": "COGS and currency conversion included; full contribution margin with shipping and fees not confirmed",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "30+ cohort KPIs, LTV, customer journeys, RFM",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "subscription_analytics": {
      "value": "yes",
      "note": "Recharge, Skio, Smartrr, Awtomic, Bold, Stay.ai connectors",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Snowflake data access via web or programmatic SQL on every plan",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom segments and custom reports; custom report builds limited by monthly credits per plan",
      "sourceUrl": "https://www.peelinsights.com/pricing"
    },
    "custom_sql": {
      "value": "partial",
      "note": "SQL runs against the Snowflake data access, not in a SQL editor inside Peel's app (none found)",
      "sourceUrl": "https://www.peelinsights.com/pricing",
      "badge": "Via Snowflake access"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Magic Dash: ask questions and Peel's AI generates a dashboard",
      "sourceUrl": "https://help.peelinsights.com/docs/magic-dashboards"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app found; Slack/email digests exist",
      "sourceUrl": "https://help.peelinsights.com/docs/getting-started-with-peel"
    }
  },
  "recentChanges": [
    {
      "date": "2026-05",
      "change": "PricingSaaS reported the $179 Core plan removed from the pricing page; Core was listed again on the live page when checked 2026-11-03",
      "sourceUrl": "https://pricingsaas.com/companies/peelinsights/diffs/2026W19"
    },
    {
      "date": "2025-06",
      "change": "Magic Dash AI dashboard documentation last updated",
      "sourceUrl": "https://help.peelinsights.com/docs/magic-dashboards"
    },
    {
      "date": "2024-05",
      "change": "Shopify acqui-hired the Peel team; CEO Nicolas Grasset became a Shopify director of product while the Peel app stayed on the App Store",
      "sourceUrl": "https://ground.news/article/shopify-has-acqui-hired-ai-driven-analytics-startup-peel-insights"
    }
  ],
  "sources": [
    {
      "title": "Peel Pricing",
      "url": "https://www.peelinsights.com/pricing"
    },
    {
      "title": "Peel: Retention Analytics on Shopify App Store",
      "url": "https://apps.shopify.com/peel-insights?locale=pl"
    },
    {
      "title": "Peel App Store reviews",
      "url": "https://apps.shopify.com/peel-insights/reviews"
    },
    {
      "title": "Peel Help Center",
      "url": "https://help.peelinsights.com"
    },
    {
      "title": "Getting Started with Peel",
      "url": "https://help.peelinsights.com/docs/getting-started-with-peel"
    },
    {
      "title": "Peel Pricing Plans (help doc)",
      "url": "https://help.peelinsights.com/docs/pricing-plans"
    },
    {
      "title": "Peel Magic Dash",
      "url": "https://help.peelinsights.com/docs/magic-dashboards"
    },
    {
      "title": "Peel Attribution",
      "url": "https://help.peelinsights.com/docs/attribution"
    },
    {
      "title": "Peel API Documentation",
      "url": "https://help.peelinsights.com/docs/api-documentation"
    },
    {
      "title": "Ground News: Shopify acqui-hires Peel Insights (aggregating Business Insider, May 2024)",
      "url": "https://ground.news/article/shopify-has-acqui-hired-ai-driven-analytics-startup-peel-insights"
    },
    {
      "title": "PricingSaaS: Peel pricing diff 2026W19 (third-party)",
      "url": "https://pricingsaas.com/companies/peelinsights/diffs/2026W19"
    }
  ],
  "lastReviewed": "2026-11-03"
};
